import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import AppLayout from '@cloudscape-design/components/app-layout';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import RadioGroup from '@cloudscape-design/components/radio-group';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Toggle from '@cloudscape-design/components/toggle';
import { beginLogin, getAlias, getIdToken } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SideNav } from '../components/SideNav';
import { APP_HEADER_ID, SUPPORT_CONTACT } from '../constants';
import { DigestCadence, DigestPreferences, getPreferences, savePreferences, UnauthorizedError } from '../api';
import { loadCapabilities } from '../capabilities';

type Phase = 'loading' | 'ready' | 'error';

const CADENCE_OPTIONS: { value: DigestCadence; label: string; description: string }[] = [
  { value: 'daily', label: 'Daily', description: 'A summary every day.' },
  { value: 'weekly', label: 'Weekly', description: 'A summary once a week.' },
  { value: 'monthly', label: 'Monthly', description: 'A summary once a month.' },
];

function withSupport(message: string): string {
  return `${message} ${SUPPORT_CONTACT}`;
}

export function SettingsPage() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [statusMessage, setStatusMessage] = useState('Loading…');
  const [alias, setAlias] = useState('');
  const [authenticated, setAuthenticated] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [cadence, setCadence] = useState<DigestCadence>('weekly');
  const [email, setEmail] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const bootstrapped = useRef(false);

  const applyPrefs = useCallback((prefs: DigestPreferences) => {
    setEnabled(prefs.enabled);
    setCadence(prefs.cadence);
    setEmail(prefs.email);
  }, []);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    void (async () => {
      const token = await getIdToken();
      if (!token) {
        await beginLogin();
        return;
      }
      setAlias(getAlias());
      setAuthenticated(true);
      void loadCapabilities().catch(() => {});
      try {
        applyPrefs(await getPreferences());
        setPhase('ready');
      } catch (err) {
        if (err instanceof UnauthorizedError) {
          await beginLogin();
          return;
        }
        setPhase('error');
        setStatusMessage(withSupport(err instanceof Error ? err.message : 'Could not load preferences.'));
      }
    })();
  }, [applyPrefs]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      applyPrefs(await savePreferences({ enabled, cadence }));
      setSaveResult({ type: 'success', text: 'Your digest preferences were saved.' });
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await beginLogin();
        return;
      }
      setSaveResult({
        type: 'error',
        text: withSupport(err instanceof Error ? err.message : 'Could not save preferences.'),
      });
    } finally {
      setSaving(false);
    }
  }, [enabled, cadence, applyPrefs]);

  return (
    <>
      <AppHeader alias={alias} authenticated={authenticated} />
      <AppLayout
        headerSelector={`#${APP_HEADER_ID}`}
        toolsHide
        navigation={<SideNav />}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { text: 'Reports', href: '/' },
              { text: 'Email preferences', href: '/settings' },
            ]}
          />
        }
        content={
          <ContentLayout header={<Header variant="h1">Email digests</Header>}>
            {phase === 'loading' && <StatusIndicator type="loading">{statusMessage}</StatusIndicator>}
            {phase === 'error' && <StatusIndicator type="error">{statusMessage}</StatusIndicator>}
            {phase === 'ready' && (
              <form onSubmit={(e) => e.preventDefault()}>
                <Form
                  actions={
                    <Button variant="primary" loading={saving} onClick={() => void onSave()} data-testid="save">
                      Save
                    </Button>
                  }
                >
                  <SpaceBetween size="l">
                    {saveResult && (
                      <Alert type={saveResult.type} dismissible onDismiss={() => setSaveResult(null)}>
                        {saveResult.text}
                      </Alert>
                    )}
                    <Container header={<Header variant="h2">Digest delivery</Header>}>
                      <SpaceBetween size="l">
                        <FormField
                          label="Email digests"
                          description="A periodic summary of report titles across your scopes. No report content is included."
                        >
                          <Toggle checked={enabled} onChange={({ detail }) => setEnabled(detail.checked)}>
                            {enabled ? 'On' : 'Off'}
                          </Toggle>
                        </FormField>

                        <FormField label="Frequency">
                          <RadioGroup
                            value={cadence}
                            onChange={({ detail }) => setCadence(detail.value as DigestCadence)}
                            items={enabled ? CADENCE_OPTIONS : CADENCE_OPTIONS.map((o) => ({ ...o, disabled: true }))}
                          />
                        </FormField>

                        <FormField
                          label="Recipient"
                          description="Digests are sent to your verified work email. This cannot be changed here."
                        >
                          <Box data-testid="recipient-email">{email || '—'}</Box>
                        </FormField>
                      </SpaceBetween>
                    </Container>
                  </SpaceBetween>
                </Form>
              </form>
            )}
          </ContentLayout>
        }
      />
    </>
  );
}
